import React from "react";
import {
  TextField,
  Button,
  FormControl,
  Typography,
  Box,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { translateApiError } from "../../utils/apiErrorTranslation";

// ===== STEP DEFINITIONS =====

// Steps for donation flow (includes payment)
export const DONATION_STEPS = [
  "email_verification",
  "payment_method",
  "payment_type",
  "form_details",
  "preview",
];

// Steps for registration only (new donors who just want to register)
export const REGISTRATION_STEPS = [
  "email_verification",
  "donor_info",
  "confirmation",
];

// ===== SDK CONFIGURATION =====

export function cloverSdkUrl() {
  const env = (process.env.REACT_APP_CLOVER_ENV || "sandbox").toLowerCase();
  return env === "production"
    ? "https://checkout.clover.com/sdk.js"
    : "https://checkout.sandbox.dev.clover.com/sdk.js";
}

export function forteJsUrl() {
  const env = (process.env.REACT_APP_FORTE_ENV || "sandbox").toLowerCase();
  return env === "production"
    ? "https://api.forte.net/js/v1"
    : "https://sandbox.forte.net/api/js/v1";
}

export function loadScript(src) {
  return new Promise((resolve, reject) => {
    if ([...document.scripts].some((s) => s.src === src)) return resolve();
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

// ===== SHARED UI COMPONENTS =====

/**
 * Clover Field Component - For hosting Clover iframe elements
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} props.fieldId - DOM element ID for Clover to mount to
 * @param {boolean} [props.isMobile=false] - Adjust styling for mobile
 */
export function CloverField({ label, fieldId, isMobile = false }) {
  return (
    <FormControl fullWidth>
      <Typography 
        component="label" 
        sx={{ 
          fontSize: isMobile ? "1rem" : "1.1rem", 
          fontWeight: 500, 
          mb: 1, 
          color: "text.primary",
          display: "block"
        }}
      >
        {label}
      </Typography>
      <Box
        id={fieldId}
        sx={(theme) => ({
          border: "2px solid",
          borderColor: "grey.400",
          borderRadius: 2,
          px: 2,
          height: isMobile ? 60 : 64,
          display: "flex",
          alignItems: "center",
          backgroundColor: "grey.50",
          transition: "border-color 120ms ease, box-shadow 120ms ease",
          "&:focus-within": {
            borderColor: "primary.main",
            boxShadow: `0 0 0 4px ${alpha(theme.palette.primary.main, 0.2)}`,
            backgroundColor: "white"
          },
          "& iframe": { width: "100%", height: "100%" },
          "& > div": { width: "100%", height: "100%" }
        })}
      />
    </FormControl>
  );
}

/**
 * Elderly-friendly text field with larger touch targets
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {boolean} [props.isMobile=false] - Adjust styling for mobile
 */
export function FriendlyTextField({ label, isMobile = false, ...props }) {
  const fontSize = isMobile ? "1.1rem" : "1.2rem";
  const padding = isMobile ? 2 : 2.5;
  
  return (
    <TextField
      {...props}
      label={label}
      fullWidth
      variant="outlined"
      InputLabelProps={{ 
        shrink: true,
        sx: { fontSize, fontWeight: 500 }
      }}
      InputProps={{
        sx: { 
          fontSize,
          "& input": { 
            py: padding,
            fontSize
          }
        },
        ...props.InputProps
      }}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
          "& fieldset": { borderWidth: 2, borderColor: "grey.400" },
          "&:hover fieldset": { borderColor: "grey.600" },
          "&.Mui-focused fieldset": { borderWidth: 2 }
        },
        ...props.sx
      }}
    />
  );
}

/**
 * Forte ACH Field Component with forte-data attribute
 * @param {Object} props
 * @param {string} props.id - Field ID
 * @param {string} props.label - Field label
 * @param {string} props.forteData - Value for forte-data attribute
 * @param {string} [props.placeholder] - Placeholder text
 * @param {string} [props.type="text"] - Input type
 * @param {boolean} [props.disabled=false] - Whether field is disabled
 * @param {boolean} [props.isMobile=false] - Adjust styling for mobile
 */
export function ForteTextField({ 
  id, 
  label, 
  forteData, 
  placeholder = "", 
  type = "text", 
  disabled = false,
  isMobile = false 
}) {
  const fontSize = isMobile ? "1.1rem" : "1.2rem";
  const padding = isMobile ? 2 : 2.5;
  
  return (
    <TextField
      id={id}
      label={label}
      fullWidth
      variant="outlined"
      type={type}
      disabled={disabled}
      InputLabelProps={{ 
        shrink: true,
        sx: { fontSize, fontWeight: 500 }
      }}
      InputProps={{
        sx: { 
          fontSize,
          "& input": { py: padding, fontSize }
        }
      }}
      inputProps={{ "forte-data": forteData, placeholder, autoComplete: "off" }}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
          "& fieldset": { borderWidth: 2, borderColor: "grey.400" },
          "&:hover fieldset": { borderColor: "grey.600" },
          "&.Mui-focused fieldset": { borderWidth: 2 }
        }
      }}
    />
  );
}

/**
 * Large, friendly button component with good touch targets
 * @param {Object} props
 * @param {React.ReactNode} props.children - Button content
 * @param {string} [props.variant="contained"] - MUI Button variant
 * @param {boolean} [props.isMobile=false] - Adjust styling for mobile
 */
export function FriendlyButton({ children, variant = "contained", isMobile = false, ...props }) {
  return (
    <Button
      variant={variant}
      fullWidth={isMobile}
      sx={{
        py: 2,
        px: isMobile ? undefined : 4,
        fontSize: isMobile ? "1.1rem" : "1.2rem",
        fontWeight: 600,
        borderRadius: 3,
        textTransform: "none",
        boxShadow: variant === "contained" ? 3 : 0,
        minHeight: isMobile ? 56 : 60,
        ...props.sx
      }}
      {...props}
    >
      {children}
    </Button>
  );
}

// ===== UTILITY FUNCTIONS =====

/**
 * Calculate surcharge value based on organization settings.
 * Stored surcharge rates may already be fractional (e.g. 0.029 for 2.9%) or expressed as whole percentages (e.g. 2.9).
 */
export function calculateSurcharge(
  surchargeEnabled,
  amount,
  paymentMethod,
  surchargeConfig = {}
) {
  if (!surchargeEnabled || !amount) return 0;

  // Handle different config formats
  let rate = 0;
  
  if (typeof surchargeConfig === "number") {
    // Direct rate value
    rate = surchargeConfig;
  } else if (surchargeConfig?.surcharge_rate !== undefined) {
    // Organization object with surcharge_rate
    rate = surchargeConfig.surcharge_rate;
  } else if (surchargeConfig?.rate !== undefined) {
    // Simple config with rate
    rate = surchargeConfig.rate;
  } else if (surchargeConfig?.card_rate !== undefined || surchargeConfig?.ach_rate !== undefined) {
    // Legacy format with separate card/ach rates
    rate = paymentMethod === "card" 
      ? (surchargeConfig.card_rate || 0) 
      : (surchargeConfig.ach_rate || 0);
  }

  if (!rate || rate <= 0) return 0;

  // A rate > 1 is assumed to be a percentage (e.g. 2.9 means 2.9%), otherwise already fractional (e.g. 0.029)
  const effectiveRate = rate > 1 ? rate / 100 : rate;

  return Math.round(Number(amount) * effectiveRate * 100) / 100;
}

/**
 * Calculate total amount including surcharge
 * @param {string|number} amount - Base donation amount
 * @param {boolean} surchargeEnabled - Whether surcharge is enabled
 * @param {number} surchargeValue - Calculated surcharge value
 * @returns {number} Total amount
 */
export function calculateTotal(amount, surchargeEnabled, surchargeValue) {
  const base = Number(amount) || 0;
  return Math.round((base + (surchargeEnabled ? surchargeValue : 0)) * 100) / 100;
}

/**
 * Get step labels for translation
 * @param {Function} t - Translation function
 * @returns {Object} Map of step names to labels
 */
export function getStepLabels(t) {
  return {
    email_verification: t("email_verification", "Verify Email"),
    payment_method: t("payment_method", "Payment Method"),
    payment_type: t("payment_type", "Payment Type"),
    form_details: t("form_details", "Details"),
    donor_info: t("donor_info", "Your Information"),
    preview: t("preview", "Review"),
    confirmation: t("confirmation", "Complete"),
  };
}

/**
 * Determine which steps to show based on donor status and intent
 * - Existing donors: Go straight to donation flow (no registration needed)
 * - New donors: Can choose to donate or just register
 * 
 * @param {boolean} isNewDonor - Whether this is a new donor
 * @param {string|null} userIntent - "donate" or "register_only" (only for new donors)
 * @returns {string[]} Array of step names
 */
export function getStepsForUser(isNewDonor, userIntent) {
  if (!isNewDonor) {
    // Existing donors skip registration and go directly to donation
    return DONATION_STEPS;
  }
  
  // New donors can choose to register only
  if (userIntent === "register_only") {
    return REGISTRATION_STEPS;
  }
  
  // New donors who want to donate
  return DONATION_STEPS;
}

/**
 * Check if user can proceed to next step
 * @param {string} currentStepName - Current step name
 * @param {Object} state - Form state
 * @returns {boolean} Whether user can proceed
 */
export function canProceedToNextStep(currentStepName, state) {
  const {
    otpVerified,
    userIntent,
    isNewDonor,
    paymentMethod,
    cardTokenized,
    achTokenized,
    amount,
    formData,
    paymentType,
    frequency,
    startDate,
  } = state;

  switch (currentStepName) {
    case "email_verification":
      return otpVerified;
    case "intent_choice":
      return userIntent !== null;
    case "payment_method":
      return (paymentMethod === "card" && cardTokenized) || (paymentMethod === "ach" && achTokenized);
    case "payment_type":
      return true;
    case "form_details": {
      const hasAmount = Number(amount) > 0;
      const hasRequiredDonorInfo = formData.first_name && formData.last_name;
      const hasSplitInfo = paymentType === "split" ? (startDate) : true;
      return hasAmount && hasRequiredDonorInfo && hasSplitInfo;
    }
    case "donor_info":
      return formData.first_name && formData.last_name;
    case "preview":
      return true;
    case "confirmation":
      return true;
    default:
      return false;
  }
}

/**
 * Build payment payload for submission
 * @param {Object} params - Parameters for building payload
 * @param {string} [params.promiseId] - Existing promise identifier required for payment
 * @returns {Object} Payment payload
 */
export function createPaymentIntentId() {
  const cryptoApi = typeof window !== "undefined" ? window.crypto : undefined;

  if (cryptoApi?.randomUUID) {
    return cryptoApi.randomUUID();
  }

  return `payment-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export function buildPaymentPayload({
  email,
  donorData,
  formData,
  amount,
  surchargeEnabled,
  surchargeValue,
  paymentMethod,
  paymentMethodId,
  paymentType,
  frequency,
  startDate,
  endDate,
  numberOfPayments,
  perPaymentAmount,
  billingAnchorDay,
  promiseId,
  paymentIntentId,
}) {
  const payload = {
    email,
    donor_id: donorData?._id,
    first_name: formData.first_name,
    last_name: formData.last_name,
    phone: formData.phone,
    address: formData.address,
    zip_code: formData.zip_code,
    amount: Number(amount),
    surcharge_amount: surchargeEnabled ? surchargeValue : 0,
    surcharge_enabled: surchargeEnabled,
    payment_method_model: paymentMethod === "card" ? "donorCard" : "donorBank",
    payment_method_id: paymentMethodId,
    payment_type: paymentMethod === "card" ? "credit_card" : "bank_transfer",
  };

  if (paymentType === "one_time") {
    payload.promise_type = "one_time_payment";
  } else {
    // Split payment
    payload.frequency = frequency;
    payload.start_date = startDate;
    payload.billing_anchor_day = billingAnchorDay || null;
    // Add split payment specific fields
    if (endDate) {
      payload.end_date = endDate;
    }
    if (numberOfPayments) {
      payload.number_of_payments = numberOfPayments;
    }
    if (perPaymentAmount) {
      payload.per_payment_amount = perPaymentAmount;
    }
  }

  if (promiseId) {
    payload.promise_id = promiseId;
  }
  if (paymentIntentId) {
    payload.payment_intent_id = paymentIntentId;
  }

  return payload;
}

/**
 * Default form data structure
 */
export const DEFAULT_FORM_DATA = {
  first_name: "",
  last_name: "",
  phone: "",
  address: "",
  zip_code: "",
};

/**
 * Preset donation amounts
 */
export const PRESET_AMOUNTS = [25, 50, 100, 250, 500];

/**
 * Frequency options for recurring donations
 * Must match backend: Weekly, Monthly, Quarterly, Yearly
 */
export const FREQUENCY_OPTIONS = [
  { value: "Weekly", labelKey: "weekly", defaultLabel: "Weekly" },
  { value: "Monthly", labelKey: "monthly", defaultLabel: "Monthly" },
  { value: "Quarterly", labelKey: "quarterly", defaultLabel: "Every 3 Months" },
  { value: "Yearly", labelKey: "yearly", defaultLabel: "Once a Year" },
];

/**
 * Recurring payment presets - User-friendly options for elderly donors
 * Values must match backend enum: Weekly, Monthly, Quarterly, Yearly
 */
export const RECURRING_PRESETS = [
  { 
    value: "Weekly", 
    labelKey: "recurring_weekly", 
    defaultLabel: "Every Week",
    description: "recurring_weekly_desc",
    defaultDescription: "Charged once per week",
  },
  { 
    value: "BiWeekly", 
    labelKey: "recurring_biweekly", 
    defaultLabel: "Every Two Weeks",
    description: "recurring_biweekly_desc",
    defaultDescription: "Charged every two weeks",
  },
  { 
    value: "Monthly", 
    labelKey: "recurring_monthly", 
    defaultLabel: "Every Month",
    description: "recurring_monthly_desc",
    defaultDescription: "Charged once per month",
  },
  { 
    value: "Quarterly", 
    labelKey: "recurring_quarterly", 
    defaultLabel: "Every 3 Months",
    description: "recurring_quarterly_desc",
    defaultDescription: "Charged 4 times per year",
  },
  { 
    value: "Yearly", 
    labelKey: "recurring_yearly", 
    defaultLabel: "Once a Year",
    description: "recurring_yearly_desc",
    defaultDescription: "Charged once per year",
  },
];

/**
 * Split payment period presets - Fixed time-based options
 * Custom option allows user to specify number of payments
 */
export const SPLIT_PERIOD_PRESETS = [
  { value: "2_months", labelKey: "split_2_months", defaultLabel: "2 Months", months: 2, frequency: "Monthly" },
  { value: "3_months", labelKey: "split_3_months", defaultLabel: "3 Months", months: 3, frequency: "Monthly" },
  { value: "4_months", labelKey: "split_4_months", defaultLabel: "4 Months", months: 4, frequency: "Monthly" },
  { value: "6_months", labelKey: "split_6_months", defaultLabel: "6 Months", months: 6, frequency: "Monthly" },
  { value: "12_months", labelKey: "split_12_months", defaultLabel: "12 Months", months: 12, frequency: "Monthly" },
  { value: "custom", labelKey: "split_custom", defaultLabel: "Custom", months: null, frequency: null },
];

/**
 * Split payment frequency options (for custom period)
 * Must match backend: Weekly, BiWeekly, Monthly, Quarterly, Yearly
 */
export const SPLIT_FREQUENCY_OPTIONS = [
  { value: "Weekly", labelKey: "weekly", defaultLabel: "Weekly" },
  { value: "BiWeekly", labelKey: "biweekly", defaultLabel: "Bi-Weekly" },
  { value: "Monthly", labelKey: "monthly", defaultLabel: "Monthly" },
  { value: "Quarterly", labelKey: "quarterly", defaultLabel: "Quarterly" },
];

/**
 * Calculate split payment schedule
 * @param {number} totalAmount - Total amount to split
 * @param {number} numberOfPayments - Number of payments to split into
 * @param {string} frequency - Payment frequency (weekly, monthly, etc.)
 * @param {string|Date} startDate - Start date for first payment
 * @returns {Object} Split payment schedule with dates and amounts
 */
export function calculateSplitSchedule(totalAmount, numberOfPayments, frequency, startDate) {
  if (!totalAmount || !numberOfPayments || numberOfPayments < 2 || !frequency || !startDate) {
    return { payments: [], perPaymentAmount: 0, totalAmount: 0 };
  }

  const baseAmount = Math.floor((totalAmount / numberOfPayments) * 100) / 100;
  const remainder = Math.round((totalAmount - (baseAmount * numberOfPayments)) * 100) / 100;
  
  const payments = [];
  let currentDate = new Date(startDate);

  for (let i = 0; i < numberOfPayments; i++) {
    // First payment gets any remainder to avoid rounding issues
    const paymentAmount = i === 0 ? baseAmount + remainder : baseAmount;
    
    payments.push({
      paymentNumber: i + 1,
      date: new Date(currentDate),
      amount: paymentAmount,
    });

    // Calculate next payment date based on frequency
    currentDate = getNextPaymentDate(currentDate, frequency);
  }

  return {
    payments,
    perPaymentAmount: baseAmount,
    totalAmount,
    numberOfPayments,
    frequency,
    startDate: new Date(startDate),
    endDate: payments.length > 0 ? payments[payments.length - 1].date : null,
  };
}

/**
 * Get next payment date based on frequency
 * @param {Date} currentDate - Current date
 * @param {string} frequency - Payment frequency (Weekly, BiWeekly, Monthly, Quarterly, Yearly)
 * @returns {Date} Next payment date
 */
export function getNextPaymentDate(currentDate, frequency) {
  const nextDate = new Date(currentDate);
  
  switch (frequency) {
    case "Weekly":
      nextDate.setDate(nextDate.getDate() + 7);
      break;
    case "BiWeekly":
      nextDate.setDate(nextDate.getDate() + 14);
      break;
    case "Monthly":
      nextDate.setMonth(nextDate.getMonth() + 1);
      break;
    case "Quarterly":
      nextDate.setMonth(nextDate.getMonth() + 3);
      break;
    case "Yearly":
      nextDate.setFullYear(nextDate.getFullYear() + 1);
      break;
    default:
      nextDate.setMonth(nextDate.getMonth() + 1); // Default to monthly
  }
  
  return nextDate;
}

/**
 * Calculate number of payments from period preset
 * @param {string} periodValue - Period preset value (e.g., "3_months")
 * @param {string} frequency - Payment frequency for custom periods (Weekly, Monthly, Quarterly)
 * @param {number} customPayments - Custom number of payments for custom periods
 * @returns {number} Number of payments
 */
export function getNumberOfPaymentsFromPreset(periodValue, frequency = "Monthly", customPayments = null) {
  const preset = SPLIT_PERIOD_PRESETS.find(p => p.value === periodValue);
  
  if (!preset) return 2; // Default minimum
  
  // For custom option, use the customPayments value
  if (preset.value === "custom" && customPayments) {
    return Math.max(2, customPayments);
  }
  
  // For fixed presets, use the months value (which equals number of monthly payments)
  return preset.months || 2;
}

/**
 * Format date for display
 * @param {Date} date - Date to format
 * @param {string} locale - Locale for formatting
 * @returns {string} Formatted date string
 */
export function formatPaymentDate(date, locale = "en-US") {
  if (!date) return "";
  return new Date(date).toLocaleDateString(locale, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// ===== FUNDRAISER EMAIL AND ERROR HELPERS =====

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const normalizeDonorEmail = (value) => String(value || "").trim().toLowerCase();

export const isValidDonorEmail = (value) => EMAIL_REGEX.test(normalizeDonorEmail(value));

export const translateFundraiserError = (t, message) => {
  if (!message) return null;
  return translateApiError(t, message);
};

